import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

function getPriceTag(yours, avg) {
  if (yours > avg * 1.2) return { label: 'HIGH', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
  if (yours < avg * 0.8) return { label: 'LOW', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' };
  return { label: 'FAIR', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };
}

function Prices() {
  const [inventory, setInventory] = useState([]);
  const [marketPrices, setMarketPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [pricesLoaded, setPricesLoaded] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetch(`${API_BASE}/api/inventories`)
      .then(res => res.json())
      .then(json => {
        setInventory(json.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  async function loadMarketPrices() {
    setLoadingPrices(true);
    const prices = {};
    // Fetch in batches of 5 to avoid hammering the API
    const chunks = [];
    for (let i = 0; i < inventory.length; i += 5) {
      chunks.push(inventory.slice(i, i + 5));
    }
    for (const chunk of chunks) {
      await Promise.all(chunk.map(async lot => {
        const type = lot.item?.type === 'M' ? 'minifig' : 'part';
        const no = lot.item?.no;
        const colorId = lot.color_id || 0;
        const cond = lot.new_or_used || 'N';
        if (!no) return;
        try {
          const res = await fetch(
            `${API_BASE}/api/price/${type}/${no}?color_id=${colorId}&new_or_used=${cond}`
          );
          const data = await res.json();
          const avg = parseFloat(data?.data?.avg_price || 0);
          if (avg > 0) prices[lot.inventory_id] = avg;
        } catch (e) {
          // skip failed lookups
        }
      }));
    }
    setMarketPrices(prices);
    setLoadingPrices(false);
    setPricesLoaded(true);
  }

  const withPrices = inventory.filter(lot =>
    pricesLoaded ? marketPrices[lot.inventory_id] !== undefined : true
  );

  const overpriced = withPrices.filter(lot => {
    const avg = marketPrices[lot.inventory_id];
    return avg && parseFloat(lot.unit_price) > avg * 1.2;
  }).length;

  const underpriced = withPrices.filter(lot => {
    const avg = marketPrices[lot.inventory_id];
    return avg && parseFloat(lot.unit_price) < avg * 0.8;
  }).length;

  const fair = withPrices.filter(lot => {
    const avg = marketPrices[lot.inventory_id];
    return avg && parseFloat(lot.unit_price) >= avg * 0.8 && parseFloat(lot.unit_price) <= avg * 1.2;
  }).length;

  const filtered = pricesLoaded
    ? inventory.filter(lot => {
        const avg = marketPrices[lot.inventory_id];
        if (!avg) return false;
        const yours = parseFloat(lot.unit_price);
        if (filter === 'over') return yours > avg * 1.2;
        if (filter === 'under') return yours < avg * 0.8;
        if (filter === 'fair') return yours >= avg * 0.8 && yours <= avg * 1.2;
        return true;
      })
    : inventory;

  function getImageUrl(lot) {
    const type = lot.item?.type;
    const no = lot.item?.no || '';
    const colorId = lot.color_id || 0;
    if (type === 'M') return `https://img.bricklink.com/ItemImage/MN/0/${no}.png`;
    return `https://img.bricklink.com/ItemImage/PN/${colorId}/${no}.png`;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      <PageHeader title="Price Checker" icon="◈" />

      {/* Stat filter cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '10px',
        marginBottom: '16px',
      }}>
        {[
          { label: 'Overpriced', value: overpriced, accent: '#dc2626', key: 'over' },
          { label: 'Fair', value: fair, accent: colors.greenDark, key: 'fair' },
          { label: 'Underpriced', value: underpriced, accent: '#2563eb', key: 'under' },
        ].map(stat => {
          const isActive = filter === stat.key;
          return (
            <div
              key={stat.key}
              onClick={() => pricesLoaded && setFilter(filter === stat.key ? 'all' : stat.key)}
              style={{
                background: isActive ? colors.navy : colors.cardBg,
                border: `1px solid ${isActive ? colors.navy : colors.border}`,
                borderBottom: `3px solid ${stat.accent}`,
                borderRadius: '6px',
                padding: '16px',
                textAlign: 'center',
                cursor: pricesLoaded ? 'pointer' : 'default',
                opacity: pricesLoaded ? 1 : 0.5,
              }}
            >
              <div style={{
                fontSize: '1.8rem',
                fontWeight: 800,
                color: isActive ? '#ffffff' : stat.accent,
              }}>{stat.value}</div>
              <div style={{
                fontSize: '0.68rem',
                color: isActive ? '#94a3b8' : colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginTop: '4px',
              }}>{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Load prices button */}
      {!pricesLoaded && !loading && (
        <button
          onClick={loadMarketPrices}
          disabled={loadingPrices}
          style={{
            width: '100%',
            padding: '14px 24px',
            background: loadingPrices ? colors.border : colors.navy,
            color: loadingPrices ? colors.textSecondary : '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.9rem',
            fontWeight: 800,
            fontFamily: "'Outfit', sans-serif",
            cursor: loadingPrices ? 'default' : 'pointer',
            marginBottom: '16px',
          }}
        >
          {loadingPrices
            ? `Loading market prices... (${Object.keys(marketPrices).length} of ${inventory.length})`
            : 'Load Market Prices'}
        </button>
      )}

      {/* Loading inventory */}
      {loading && (
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px',
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: '0.9rem',
        }}>Loading inventory...</div>
      )}

      {/* Pre-load state */}
      {!loading && !pricesLoaded && !loadingPrices && (
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.navy }}>◈</div>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>
            {inventory.length} lots loaded
          </div>
          <div style={{ fontSize: '0.82rem', color: colors.textSecondary }}>
            Click "Load Market Prices" to fetch 6-month averages from BrickLink and compare against your prices
          </div>
        </div>
      )}

      {/* Part rows */}
      {!loading && pricesLoaded && filtered.map((lot, i) => {
        const avg = marketPrices[lot.inventory_id];
        const yours = parseFloat(lot.unit_price);
        const tag = getPriceTag(yours, avg);
        const diff = avg ? ((yours - avg) / avg * 100).toFixed(0) : 0;
        return (
          <div key={i} style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '14px',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <img
              src={getImageUrl(lot)}
              alt={lot.item?.name || ''}
              style={{
                width: '48px',
                height: '48px',
                objectFit: 'contain',
                background: '#f9fafb',
                borderRadius: '6px',
                padding: '4px',
                border: `1px solid ${colors.border}`,
              }}
              onError={(e) => { e.target.style.opacity = '0.15'; }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '0.88rem',
                fontWeight: 800,
                color: colors.textPrimary,
                marginBottom: '3px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>{lot.item?.name || 'Unknown'}</div>
              <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>
                {lot.item?.no} · {lot.color_name} · Qty: {lot.quantity}
              </div>
            </div>
            <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <div style={{ fontSize: '1rem', fontWeight: 800, color: colors.textPrimary }}>
                ${yours.toFixed(2)}
              </div>
              <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>
                Avg: ${avg ? avg.toFixed(2) : '—'}
              </div>
            </div>
            <div style={{
              background: tag.bg,
              border: `1px solid ${tag.border}`,
              borderRadius: '4px',
              padding: '4px 10px',
              fontSize: '0.66rem',
              fontWeight: 700,
              color: tag.color,
              minWidth: '60px',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}>
              {tag.label} {Number(diff) > 0 ? `+${diff}%` : `${diff}%`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Prices;